import { ReactNode, useEffect, useRef, useState } from "react";
import { BsArrowReturnRight } from "react-icons/bs";

import {
  DatePicker,
  DateRange,
  DateRangePicker,
} from "@/components/ui/date-picker";
import Input from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { FilterProps } from "./types";

const DateInput: React.FC<FilterProps> = ({
  value,
  onChange,
  allowFutureDates = false,
}) => {
  const [selectedDatePicker, setSelectedDatePicker] = useState("1");
  const [inTheLast, setInTheLast] = useState<number | undefined>(undefined);
  const [exactDate, setExactDate] = useState<Date>();
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  // Reset internal state when value is cleared externally (e.g. "Clear" button)
  const prevValue = useRef(value);
  useEffect(() => {
    if (prevValue.current !== undefined && value === undefined) {
      setSelectedDatePicker("1");
      setInTheLast(undefined);
      setExactDate(undefined);
      setDateRange(undefined);
    }
    prevValue.current = value;
  }, [value]);

  const formatDateTimeToISO = (date: Date) => {
    let day = "" + date.getDate();
    let month = "" + (date.getMonth() + 1); // getMonth() is zero-based
    const year = date.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  };

  const handleSelectedDatePicker = async (value: string) => {
    await Promise.all([
      setExactDate(undefined),
      setInTheLast(undefined),
      setDateRange(undefined),
    ]);

    setSelectedDatePicker(value);
  };

  const handleInTheLastDays = (days: number) => {
    setInTheLast(days);
    if (days && days !== 0) {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days + 1);

      onChange([
        formatDateTimeToISO(startDate),
        formatDateTimeToISO(new Date()),
      ]);
    }
  };

  const handleDateSelector = (
    date: Date | undefined,
    type: "exact" | "before" | "onOrAfter",
  ) => {
    if (date) {
      setExactDate(date);
      const dateStr = formatDateTimeToISO(date);
      if (type === "exact") {
        onChange([dateStr, dateStr]);
      } else if (type === "onOrAfter") {
        onChange([dateStr, "9000-12-31"]);
      } else if (type === "before") {
        onChange(["1970-01-01", dateStr]);
      }
    } else {
      setExactDate(undefined);
    }
  };

  const handleDateRangeSelector = (range: DateRange | undefined) => {
    if (range?.from && range.to) {
      setDateRange(range);
      const start = range.from;
      const end = range.to;
      if (start && end) {
        const startDate = formatDateTimeToISO(start);
        const endDate = formatDateTimeToISO(end);
        onChange([startDate, endDate]);
      }
    } else if (!range?.from && !range?.to) {
      setDateRange(undefined);
    }
  };

  const DatePickerArrow = ({ children }: { children: ReactNode }) => (
    <div className="mt-2.5 flex items-center">
      <BsArrowReturnRight className="ml-1.5 mr-2 h-5 w-4 flex-shrink-0 text-primary" />
      {children}
    </div>
  );

  const generateDatePicker = (selectedDatePicker: string) => {
    let datePickerComponent;

    switch (selectedDatePicker) {
      case "1":
        datePickerComponent = (
          <>
            <Input
              type="number"
              value={inTheLast}
              onChange={(e) => {
                const days = parseInt(e.target.value, 10);
                if (!isNaN(days) && days >= 1 && days <= 99) {
                  handleInTheLastDays(days);
                }
              }}
              className="max-h-8 min-w-5 overflow-hidden text-caption"
              min={1}
              max={99}
            />
            <span className="pl-2 text-body text-foreground">days</span>
          </>
        );
        break;
      case "2":
      case "4":
      case "5":
        datePickerComponent = (
          <DatePicker
            value={exactDate}
            placeholder=""
            toDate={allowFutureDates ? undefined : new Date()}
            onChange={(v) =>
              handleDateSelector(
                v,
                selectedDatePicker === "2"
                  ? "exact"
                  : selectedDatePicker === "4"
                    ? "onOrAfter"
                    : "before",
              )
            }
          />
        );
        break;
      case "3":
        datePickerComponent = (
          <DateRangePicker
            value={dateRange}
            placeholder=""
            toDate={allowFutureDates ? undefined : new Date()}
            onChange={handleDateRangeSelector}
          />
        );
        break;
      default:
        return null;
    }

    return <DatePickerArrow>{datePickerComponent}</DatePickerArrow>;
  };

  return (
    <div className="mx-auto max-w-xs">
      <Select
        value={selectedDatePicker}
        onValueChange={handleSelectedDatePicker}
      >
        <SelectTrigger className="mt-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="1">is in the last</SelectItem>
          <SelectItem value="2">is equal to</SelectItem>
          <SelectItem value="3">is between</SelectItem>
          <SelectItem value="4">is on or after</SelectItem>
          <SelectItem value="5">is before</SelectItem>
        </SelectContent>
      </Select>
      {generateDatePicker(selectedDatePicker)}
    </div>
  );
};

export default DateInput;
